// src/components/services/authService.ts - Google & Email/Password Authentication
import { 
  getAuth, 
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  onAuthStateChanged,
  User,
  updateProfile
} from 'firebase/auth';
import { 
  doc, 
  getDoc
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { FREE_TIER_MAX_VIDEOS } from '../../constants';
import { STRONG_PASSWORD_REQUIREMENTS, validateStrongPassword } from './passwordPolicy';
import {
  ensureBackendUserProfile,
  syncBackendLibraryCounters
} from './userProfileFunctions';

const auth = getAuth();

// Initialize Google provider
const googleProvider = new GoogleAuthProvider();

const mapGoogleAuthError = (error: any): string => {
  const code = error?.code as string | undefined;
  const origin = typeof window !== 'undefined' ? window.location.origin : 'this domain';

  switch (code) {
    case 'auth/unauthorized-domain':
      return `Google sign-in is not enabled for ${origin}. Add this domain in Firebase Console -> Authentication -> Settings -> Authorized domains, then retry.`;
    case 'auth/operation-not-allowed':
      return 'Google sign-in is disabled in Firebase Authentication. Enable Google provider in Firebase Console -> Authentication -> Sign-in method.';
    case 'auth/popup-blocked':
      return 'Google sign-in popup was blocked by your browser. Allow popups for this site and try again.';
    case 'auth/popup-closed-by-user':
      return 'Google sign-in popup was closed before completing authentication.';
    case 'auth/cancelled-popup-request':
      return 'Another sign-in popup request is already in progress. Close extra popups and try again.';
    case 'auth/network-request-failed':
      return 'Network error during Google sign-in. Check your connection and try again.';
    default:
      return error?.message
        ? `Google sign-in failed: ${error.message}`
        : 'Failed to sign in with Google';
  }
};

export interface UserProfile {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  libraryCount: number;
  libraryLimit: number;
  isPremium: boolean;
  storageExpiresAt?: Date;
  createdAt: Date;
  lastLogin: Date;
  authProvider: string;
  
  // NEW: Sharing tracking fields
  activeSharesCount: number;
  lastShareCreatedAt?: Date;
  totalSharesCreated?: number;
  totalShareViews?: number;
}

// Current free-tier baseline. Existing profile documents can still contain the
// old 5-slot limit; normalize those upward so UI and enforcement agree.
const CURRENT_FREE_TIER_LIMIT = FREE_TIER_MAX_VIDEOS;

const toNonNegativeNumber = (value: unknown): number | null => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }
  return Math.max(0, value);
};

const resolveExistingUserLibraryState = (data: Record<string, any>) => {
  const libraryCount =
    toNonNegativeNumber(data.libraryCount) ??
    toNonNegativeNumber(data.sessionCount) ??
    0;

  const storedLimit = toNonNegativeNumber(data.libraryLimit);
  let libraryLimit: number;

  // Migration guard: never show a limit below the current free tier or below
  // the number of already-saved upload-backed videos.
  libraryLimit = Math.max(
    storedLimit ?? CURRENT_FREE_TIER_LIMIT,
    CURRENT_FREE_TIER_LIMIT,
    libraryCount
  );

  const shouldBackfillLimit = storedLimit !== libraryLimit;

  return {
    libraryCount,
    libraryLimit,
    shouldBackfillLimit
  };
};

const toDate = (value: any, fallback = new Date()): Date => {
  if (value instanceof Date) {
    return value;
  }

  const converted = value?.toDate?.();
  return converted instanceof Date ? converted : fallback;
};

const buildUserProfile = (
  uid: string,
  data: Record<string, any>
): UserProfile => {
  const { libraryCount, libraryLimit } = resolveExistingUserLibraryState(data);

  return {
    uid,
    email: data.email,
    displayName: data.displayName,
    photoURL: data.photoURL,
    libraryCount,
    libraryLimit,
    isPremium: data.isPremium || false,
    createdAt: toDate(data.createdAt),
    lastLogin: toDate(data.lastLogin),
    authProvider: data.authProvider || 'unknown',

    // NEW: Load sharing fields (with defaults for backwards compatibility)
    activeSharesCount: data.activeSharesCount ?? 0,
    lastShareCreatedAt: data.lastShareCreatedAt?.toDate?.(),
    totalSharesCreated: data.totalSharesCreated ?? 0,
    totalShareViews: data.totalShareViews ?? 0
  };
};

/**
 * Sign in with Google
 */
export const signInWithGoogle = async (): Promise<UserProfile> => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;
    const userProfile = await createOrUpdateUserProfile(user, 'google');
    return userProfile;
  } catch (error) {
    console.error('Error signing in with Google:', error);
    throw new Error(mapGoogleAuthError(error));
  }
};

/**
 * Sign in with Email/Password
 */
export const signInWithEmail = async (email: string, password: string): Promise<UserProfile> => {
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    const user = result.user;
    const userProfile = await createOrUpdateUserProfile(user, 'email');
    return userProfile;
  } catch (error: any) {
    console.error('Error signing in with email:', error);
    if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
      throw new Error('Invalid email or password');
    }
    throw new Error('Failed to sign in');
  }
};

/**
 * Sign up with Email/Password
 */
export const signUpWithEmail = async (
  email: string, 
  password: string, 
  displayName: string
): Promise<UserProfile> => {
  const passwordValidation = validateStrongPassword(password, {
    email,
    displayName
  });
  if (!passwordValidation.isValid) {
    throw new Error(passwordValidation.message || STRONG_PASSWORD_REQUIREMENTS);
  }

  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    const user = result.user;
    await updateProfile(user, { displayName });
    const userProfile = await createOrUpdateUserProfile(user, 'email', displayName);
    return userProfile;
  } catch (error: any) {
    console.error('Error signing up with email:', error);
    if (error.code === 'auth/email-already-in-use') {
      throw new Error('Email already in use');
    } else if (error.code === 'auth/weak-password') {
      throw new Error(STRONG_PASSWORD_REQUIREMENTS);
    } else if (error.code === 'auth/invalid-email') {
      throw new Error('Invalid email address');
    }
    throw new Error('Failed to create account');
  }
};

/**
 * Send password reset email
 */
export const resetPassword = async (email: string): Promise<void> => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error: any) {
    console.error('Error sending password reset email:', error);
    if (error.code === 'auth/user-not-found') {
      throw new Error('No account found with this email');
    }
    throw new Error('Failed to send password reset email');
  }
};

/**
 * Sign out
 */
export const signOut = async (): Promise<void> => {
  try {
    await firebaseSignOut(auth);
  } catch (error) {
    console.error('Error signing out:', error);
    throw new Error('Failed to sign out');
  }
};

/**
 * Create or update user profile in Firestore
 * NOW INCLUDES: Sharing tracking fields
 */
const createOrUpdateUserProfile = async (
  user: User, 
  provider: string,
  customDisplayName?: string
): Promise<UserProfile> => {
  await ensureBackendUserProfile({
    authProvider: provider,
    displayName: customDisplayName || user.displayName || undefined,
    email: user.email || undefined,
    photoURL: user.photoURL || undefined
  });
  await syncBackendLibraryCounters();

  const userRef = doc(db, 'users', user.uid);
  const userDoc = await getDoc(userRef);

  if (!userDoc.exists()) {
    throw new Error('User profile was not created. Please try signing in again.');
  }

  console.log('✅ Synced user profile with backend-owned fields');
  return buildUserProfile(user.uid, userDoc.data());
};

/**
 * Get current user profile
 */
export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser || currentUser.uid !== uid) {
      return null;
    }

    await ensureBackendUserProfile({
      displayName: currentUser.displayName || undefined,
      email: currentUser.email || undefined,
      photoURL: currentUser.photoURL || undefined
    });
    await syncBackendLibraryCounters();

    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return null;
    }
    
    const data = userDoc.data();
    return buildUserProfile(uid, data);
  } catch (error) {
    console.error('Error getting user profile:', error);
    return null;
  }
};

/**
 * Check if user can save a session
 */
export const canSaveSession = async (uid: string): Promise<boolean> => {
  const profile = await getUserProfile(uid);
  if (!profile) return false;
  return profile.isPremium || profile.libraryCount < profile.libraryLimit;
};

/**
 * Increment user's library count
 */
export const incrementLibraryCount = async (uid: string): Promise<void> => {
  try {
    if (auth.currentUser?.uid !== uid) {
      throw new Error('Not authorized to sync this library count');
    }

    const { libraryCount } = await syncBackendLibraryCounters();
    console.log('🟢 Library count synced successfully:', libraryCount);
  } catch (error: any) {
    console.error('🔴 Error in incrementLibraryCount:', error);
    console.error('🔴 Error code:', error?.code);
    console.error('🔴 Error message:', error?.message);
    throw error;
  }
};

// Keep old function name as alias for backwards compatibility
export const incrementSessionCount = incrementLibraryCount;

/**
 * Decrement user's library count
 */
export const decrementLibraryCount = async (uid: string): Promise<void> => {
  try {
    if (auth.currentUser?.uid !== uid) {
      throw new Error('Not authorized to sync this library count');
    }

    const { libraryCount } = await syncBackendLibraryCounters();
    console.log('🟢 Library count synced successfully:', libraryCount);
  } catch (error: any) {
    console.error('🔴 Error in decrementLibraryCount:', error);
    throw error;
  }
};

/**
 * Reset library count to match actual number of videos
 */
export const syncLibraryCount = async (uid: string): Promise<void> => {
  try {
    if (auth.currentUser?.uid !== uid) {
      throw new Error('Not authorized to sync this library count');
    }

    const { libraryCount } = await syncBackendLibraryCounters();
    console.log('🟢 Library count synced successfully:', libraryCount);
  } catch (error) {
    console.error('🔴 Error syncing library count:', error);
    throw error;
  }
};

/**
 * Listen to auth state changes
 */
export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

/**
 * Get current user
 */
export const getCurrentUser = (): User | null => {
  return auth.currentUser;
};

export { auth };
